// src/pages/Requests.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../api/axios';

import {
  FiInbox,
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiMoreHorizontal,
  FiTrash2,
  FiUserPlus,
  FiUserX,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiExternalLink,
} from 'react-icons/fi';

/* ----------------------------- helpers ----------------------------- */

const resolveId = (row) => row?.requestId || row?._id || row?.id;

const getReqNumber = (row) =>
  row?.requestNumber || row?.number || row?.ref || row?.code || '—';

const getContactName = (row) =>
  row?.contactName ||
  row?.name ||
  row?.fullName ||
  row?.customerName ||
  row?.leadName ||
  '—';

const getEmail = (row) => row?.email || row?.contactEmail || row?.fromEmail || '';
const getPhone = (row) => row?.phone || row?.contactPhone || row?.fromPhone || '';

const getPreview = (row) => {
  const raw =
    row?.summary ||
    row?.message ||
    row?.notes ||
    row?.details ||
    row?.description ||
    '';
  return String(raw || '').trim();
};

const getSource = (row) => String(row?.source || row?.channel || 'unknown').toLowerCase();

const getStatus = (row) => String(row?.status || 'new').toLowerCase();

const getAssignedTo = (row) => row?.assignedTo || row?.owner || row?.assignee || null;

// assignedTo might be id, or object
const assignedId = (assigned) =>
  typeof assigned === 'string'
    ? assigned
    : assigned?._id || assigned?.id || assigned?.userId || null;

const assignedName = (assigned) => {
  if (!assigned) return 'Unassigned';
  if (typeof assigned === 'string') return 'Assigned';
  return (
    assigned?.name ||
    assigned?.fullName ||
    assigned?.email ||
    assigned?.displayName ||
    'Assigned'
  );
};

function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function statusLabel(s) {
  const v = String(s || '').toLowerCase();
  if (v === 'new') return 'New';
  if (v === 'contacted') return 'Contacted';
  if (v === 'qualified') return 'Qualified';
  if (v === 'unqualified') return 'Unqualified';
  if (v === 'converted') return 'Converted';
  if (v === 'spam') return 'Spam';
  if (v === 'closed') return 'Closed';
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : '—';
}

function statusChipClass(v) {
  const s = String(v || '').toLowerCase();
  switch (s) {
    case 'new':
      return 'bg-blue-50 text-blue-700 border border-blue-200';
    case 'contacted':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'qualified':
      return 'bg-green-50 text-green-700 border border-green-200';
    case 'unqualified':
      return 'bg-slate-100 text-slate-700 border border-slate-200';
    case 'converted':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'spam':
      return 'bg-rose-50 text-rose-700 border border-rose-200';
    case 'closed':
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    default:
      return 'bg-slate-100 text-slate-700 border border-slate-200';
  }
}

const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Qualified', value: 'qualified' },
  { label: 'Unqualified', value: 'unqualified' },
  { label: 'Converted', value: 'converted' },
  { label: 'Spam', value: 'spam' },
  { label: 'Closed', value: 'closed' },
];

const SOURCE_OPTIONS = [
  { label: 'All sources', value: 'all' },
  { label: 'Web', value: 'web' },
  { label: 'Form', value: 'form' },
  { label: 'Email', value: 'email' },
  { label: 'Manual', value: 'manual' },
  { label: 'Unknown', value: 'unknown' },
];

function isOpenStatus(status) {
  const s = String(status || '').toLowerCase();
  return !['converted', 'spam', 'closed'].includes(s);
}

/* ----------------------------- Action menu (portaled) ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  onClose,
  canMutate,
  canDelete,
  users,
  meId,
  onView,
  onSetStatus,
  onAssign,
  onDelete,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;

    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;
    const menuW = 260;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setPos((p) => ({ ...p, top, left, origin }));

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setPos({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = resolveId(row);
  const currentStatus = getStatus(row);
  const currentAssigned = assignedId(getAssignedTo(row));

  const topUsers = Array.isArray(users) ? users.slice(0, 8) : [];
  const hasUsers = Array.isArray(users) && users.length > 0;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 60 }}
      className={`min-w-[260px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${pos.origin}`}
    >
      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={() => {
          onClose();
          onView(id);
        }}
      >
        <FiExternalLink /> View details
      </button>

      <div className="my-1 h-px bg-slate-100" />

      {/* Status quick actions */}
      <div className="px-3 py-2">
        <div className="text-xs font-semibold text-slate-500 mb-2">Set status</div>
        <div className="grid grid-cols-2 gap-2">
          {STATUS_OPTIONS.slice(0, 4).map((s) => (
            <button
              key={s.value}
              disabled={!canMutate || currentStatus === s.value}
              className={`px-2 py-1.5 rounded-lg border text-xs font-semibold ${
                currentStatus === s.value
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white hover:bg-slate-50 border-slate-200'
              } ${!canMutate ? 'opacity-40 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (!canMutate) return;
                onClose();
                onSetStatus(id, s.value);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-2 grid grid-cols-3 gap-2">
          {['converted', 'spam', 'closed'].map((v) => (
            <button
              key={v}
              disabled={!canMutate || currentStatus === v}
              className={`px-2 py-1.5 rounded-lg border text-xs font-semibold ${
                currentStatus === v
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white hover:bg-slate-50 border-slate-200'
              } ${!canMutate ? 'opacity-40 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (!canMutate) return;
                onClose();
                onSetStatus(id, v);
              }}
            >
              {statusLabel(v)}
            </button>
          ))}
        </div>
      </div>

      <div className="my-1 h-px bg-slate-100" />

      {/* Assign */}
      <div className="px-3 py-2">
        <div className="text-xs font-semibold text-slate-500 mb-2">Assign</div>

        <div className="flex gap-2">
          <button
            disabled={!canMutate || !meId || currentAssigned === meId}
            className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-semibold flex items-center justify-center gap-2 ${
              !canMutate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'
            }`}
            onClick={() => {
              if (!canMutate || !meId) return;
              onClose();
              onAssign(id, meId);
            }}
            title="Assign to me"
          >
            <FiUserPlus /> Me
          </button>

          <button
            disabled={!canMutate || !currentAssigned}
            className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-semibold flex items-center justify-center gap-2 ${
              !canMutate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'
            }`}
            onClick={() => {
              if (!canMutate) return;
              onClose();
              onAssign(id, null);
            }}
            title="Unassign"
          >
            <FiUserX /> Unassign
          </button>
        </div>

        {hasUsers && (
          <div className="mt-2 max-h-44 overflow-auto rounded-lg border border-slate-200">
            {topUsers.map((u) => {
              const uid = u?._id || u?.id || u?.userId;
              const uname = u?.name || u?.fullName || u?.email || 'User';
              const active = uid && currentAssigned === String(uid);
              return (
                <button
                  key={String(uid)}
                  disabled={!canMutate || !uid || active}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                    active ? 'bg-blue-50' : ''
                  } ${!canMutate ? 'opacity-40 cursor-not-allowed' : ''}`}
                  onClick={() => {
                    if (!canMutate || !uid) return;
                    onClose();
                    onAssign(id, String(uid));
                  }}
                >
                  <div className="font-semibold text-slate-900">{uname}</div>
                  <div className="text-xs text-slate-500">{u?.role ? String(u.role) : ''}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {canDelete && (
        <>
          <div className="my-1 h-px bg-slate-100" />
          <button
            role="menuitem"
            className="w-full px-3 py-2 text-left text-rose-600 hover:bg-rose-50 flex items-center gap-2"
            onClick={() => {
              onClose();
              onDelete(row);
            }}
          >
            <FiTrash2 /> Delete
          </button>
        </>
      )}
    </div>,
    document.body
  );
}

/* ----------------------------- Bulk actions bar ----------------------------- */

function BulkActionsBar({
  selectedCount,
  canDelete,
  onClear,
  onBulkStatus,
  onBulkAssignMe,
  onBulkUnassign,
  onBulkDelete,
}) {
  const [status, setStatus] = useState('contacted');

  return (
    <div className="fixed top-16 left-0 right-0 z-50 flex flex-wrap gap-3 items-center justify-between bg-blue-50/90 backdrop-blur border-b border-blue-200 px-6 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="font-semibold text-blue-800">
          {selectedCount} selected
        </span>
        <button
          className="text-sm text-blue-700 hover:underline"
          onClick={onClear}
        >
          Clear
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
          <span className="text-sm text-gray-600">Status:</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="text-sm bg-white appearance-none pr-6"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => onBulkStatus(status)}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Apply
          </button>
        </div>

        <button
          onClick={onBulkAssignMe}
          className="rounded-lg border bg-white px-3 py-2 text-sm hover:bg-gray-50"
          title="Assign selected to me"
        >
          <FiUserPlus className="inline -mt-0.5 mr-2" />
          Assign to me
        </button>

        <button
          onClick={onBulkUnassign}
          className="rounded-lg border bg-white px-3 py-2 text-sm hover:bg-gray-50"
          title="Unassign selected"
        >
          <FiUserX className="inline -mt-0.5 mr-2" />
          Unassign
        </button>

        {canDelete && (
          <button
            className="bg-rose-600 text-white px-3 py-2 rounded-lg hover:bg-rose-700 font-semibold"
            onClick={onBulkDelete}
          >
            <FiTrash2 className="inline mr-2 -mt-0.5" /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- main page ----------------------------- */

export default function Requests() {
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userRole, setUserRole] = useState('viewer');
  const [meId, setMeId] = useState(null);

  const canMutate = userRole === 'admin' || userRole === 'staff' || userRole === 'owner';
  const canDelete = userRole === 'admin' || userRole === 'owner';

  // team users (best-effort)
  const [users, setUsers] = useState([]);

  // filters/sort/pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // open | all | specific status
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('any'); // any | me | unassigned | userId

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [selected, setSelected] = useState([]);
  const [focusedId, setFocusedId] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const searchRef = useRef(null);

  /* ----------------------------- data fetch ----------------------------- */

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/profile');
      const p = res?.data?.data || res?.data || {};
      const role = String(p?.role || 'viewer').toLowerCase();
      setUserRole(role);
      setMeId(String(p?._id || p?.id || p?.userId || '') || null);
      try {
        localStorage.setItem('profile', JSON.stringify(p || {}));
      } catch {}
    } catch {
      try {
        const p = JSON.parse(localStorage.getItem('profile') || '{}');
        const role = String(p?.role || 'viewer').toLowerCase();
        setUserRole(role);
        setMeId(String(p?._id || p?.id || p?.userId || '') || null);
      } catch {
        setUserRole('viewer');
        setMeId(null);
      }
    }
  }, []);

  const normalizeList = (resp) => {
    const d = resp?.data?.data ?? resp?.data ?? resp;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.results)) return d.results;
    return [];
  };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      // Pass params (backend may ignore). We still client-filter for safety.
      const resp = await api.get('/requests', {
        params: {
          page,
          limit: pageSize,
          sortBy,
          sortOrder: sortDir,
        },
      });
      const items = normalizeList(resp);
      setRequests(items);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || 'Failed to load requests');
      setRequests([]);
    } finally {
      setLoading(false);
      setSelected([]);
    }
  }, [page, pageSize, sortBy, sortDir]);

  const fetchUsers = useCallback(async () => {
    if (!canMutate) return; // viewer doesn’t need
    try {
      // Best-effort — your /api/users may differ.
      const res = await api.get('/users', { params: { limit: 100 } });
      const d = res?.data?.data ?? res?.data;
      const items = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : Array.isArray(d?.data) ? d.data : [];
      setUsers(Array.isArray(items) ? items : []);
    } catch {
      setUsers([]);
    }
  }, [canMutate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /* ----------------------------- derived: filter/sort/paginate ----------------------------- */

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      const name = getContactName(r).toLowerCase();
      const email = getEmail(r).toLowerCase();
      const phone = getPhone(r).toLowerCase();
      const prev = getPreview(r).toLowerCase();
      const num = String(getReqNumber(r)).toLowerCase();
      const src = getSource(r);
      const st = getStatus(r);

      const matchesSearch =
        !q ||
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        prev.includes(q) ||
        num.includes(q);

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'open'
          ? isOpenStatus(st)
          : st === statusFilter;

      const matchesSource = sourceFilter === 'all' ? true : src === sourceFilter;

      const a = getAssignedTo(r);
      const aid = assignedId(a);
      const matchesAssignee =
        assigneeFilter === 'any'
          ? true
          : assigneeFilter === 'me'
          ? !!meId && !!aid && String(aid) === String(meId)
          : assigneeFilter === 'unassigned'
          ? !aid
          : !!aid && String(aid) === String(assigneeFilter);

      return matchesSearch && matchesStatus && matchesSource && matchesAssignee;
    });
  }, [requests, search, statusFilter, sourceFilter, assigneeFilter, meId]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;

      if (sortBy === 'createdAt') {
        const A = new Date(a?.createdAt || a?.created || 0).getTime();
        const B = new Date(b?.createdAt || b?.created || 0).getTime();
        return (A - B) * dir;
      }

      if (sortBy === 'status') {
        const A = getStatus(a);
        const B = getStatus(b);
        return A.localeCompare(B) * dir;
      }

      if (sortBy === 'contact') {
        const A = getContactName(a).toLowerCase();
        const B = getContactName(b).toLowerCase();
        return A.localeCompare(B) * dir;
      }

      // fallback
      const A = String(a?.[sortBy] ?? '').toLowerCase();
      const B = String(b?.[sortBy] ?? '').toLowerCase();
      return A.localeCompare(B) * dir;
    });
    return list;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = clamp(page, 1, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = sorted.slice(startIdx, startIdx + pageSize);

  // selection
  const allIds = filtered.map((r) => resolveId(r)).filter(Boolean);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const isPartialSelected = selected.length > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelected([]);
    else setSelected(Array.from(new Set([...selected, ...allIds])));
  };

  const toggleRow = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  /* ----------------------------- stats ----------------------------- */

  const counts = useMemo(() => {
    const acc = {
      total: filtered.length,
      open: 0,
      new: 0,
      qualified: 0,
      converted: 0,
      spam: 0,
    };
    for (const r of filtered) {
      const st = getStatus(r);
      if (isOpenStatus(st)) acc.open++;
      if (st === 'new') acc.new++;
      if (st === 'qualified') acc.qualified++;
      if (st === 'converted') acc.converted++;
      if (st === 'spam') acc.spam++;
    }
    return acc;
  }, [filtered]);

  /* ----------------------------- mutations ----------------------------- */

  const patchRequest = async (id, payload) => {
    try {
      await api.put(`/requests/${id}`, payload);
      setRequests((prev) =>
        prev.map((r) => (resolveId(r) === id ? { ...r, ...payload } : r))
      );
      toast.success('Updated');
    } catch (e) {
      toast.error(e?.message || 'Update failed');
      throw e;
    }
  };

  const deleteRequest = async (row) => {
    const id = resolveId(row);
    if (!id) return;
    if (!canDelete) return toast.error('Not allowed');
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    try {
      await api.delete(`/requests/${id}`);
      toast.success('Deleted');
      fetchRequests();
    } catch (e) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  const bulkPatch = async (ids, payload) => {
    if (!ids.length) return;
    try {
      await Promise.all(
        ids.map((id) => api.put(`/requests/${id}`, payload).catch(() => null))
      );
      toast.success('Bulk update complete');
      fetchRequests();
    } catch {
      toast.error('Bulk update failed');
    }
  };

  /* ----------------------------- keyboard shortcuts ----------------------------- */

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;

      if (e.key === 'f' || e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSelected([]);
        setFocusedId(null);
        setMenuAnchor(null);
        setMenuRow(null);
      }
      if (e.key === 'j' || e.key === 'k') {
        if (!pageRows.length) return;
        e.preventDefault();
        const idx = focusedId ? pageRows.findIndex((r) => resolveId(r) === focusedId) : -1;
        const nextIdx =
          e.key === 'j'
            ? Math.min(pageRows.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        setFocusedId(resolveId(pageRows[nextIdx]));
      }
      if (e.key === 'o' && focusedId) navigate(`/requests/${focusedId}`);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && canMutate) {
        e.preventDefault();
        setSelected(allIds);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [pageRows, focusedId, allIds, canMutate]);

  /* ----------------------------- UI ----------------------------- */

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header */}
      <div className="rounded-2xl mb-6 border border-slate-200 bg-white shadow-sm">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FiInbox />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Requests
                </h1>
              </div>
              <p className="text-slate-600 mt-1">
                Inbox for new inquiries. Triage, assign, and convert.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={fetchRequests}
                className="inline-flex items-center gap-2 border bg-white px-4 py-2 rounded-xl font-semibold hover:bg-slate-50 transition"
                title="Refresh"
              >
                <FiClock /> Refresh
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiInbox /> Filtered
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.total}</div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiAlertCircle /> Open
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.open}</div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiClock /> New
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.new}</div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiCheckCircle /> Qualified
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.qualified}</div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Converted</div>
              <div className="text-lg font-bold text-slate-900">{counts.converted}</div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Role</div>
              <div className="text-lg font-bold text-slate-900 capitalize">{userRole}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="relative w-full lg:w-[420px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search name, email, phone, request #, message…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-stretch lg:justify-end">
              {/* Status */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  title="Status filter"
                >
                  <option value="open">Open</option>
                  <option value="all">All</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Source */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sourceFilter}
                  onChange={(e) => {
                    setSourceFilter(e.target.value);
                    setPage(1);
                  }}
                  title="Source filter"
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Assignee */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={assigneeFilter}
                  onChange={(e) => {
                    setAssigneeFilter(e.target.value);
                    setPage(1);
                  }}
                  title="Assignee filter"
                >
                  <option value="any">Any assignee</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="me">Assigned to me</option>
                  {Array.isArray(users) &&
                    users.slice(0, 50).map((u) => {
                      const uid = String(u?._id || u?.id || u?.userId || '');
                      const uname = u?.name || u?.fullName || u?.email || 'User';
                      if (!uid) return null;
                      return (
                        <option key={uid} value={uid}>
                          {uname}
                        </option>
                      );
                    })}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  title="Sort by"
                >
                  <option value="createdAt">Created</option>
                  <option value="status">Status</option>
                  <option value="contact">Contact</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value);
                    setPage(1);
                  }}
                  title="Sort direction"
                >
                  <option value="desc">Newest</option>
                  <option value="asc">Oldest</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Page size */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  title="Rows per page"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {canMutate && selected.length > 0 && (
        <BulkActionsBar
          selectedCount={selected.length}
          canDelete={canDelete}
          onClear={() => setSelected([])}
          onBulkStatus={(status) => bulkPatch(selected, { status })}
          onBulkAssignMe={() => {
            if (!meId) return toast.error('Profile missing');
            bulkPatch(selected, { assignedTo: meId });
          }}
          onBulkUnassign={() => bulkPatch(selected, { assignedTo: null })}
          onBulkDelete={async () => {
            if (!canDelete) return toast.error('Not allowed');
            if (!window.confirm('Delete selected requests? This cannot be undone.')) return;
            try {
              await Promise.all(
                selected.map((id) => api.delete(`/requests/${id}`).catch(() => null))
              );
              toast.success('Deleted selected');
              fetchRequests();
            } catch {
              toast.error('Bulk delete failed');
            }
          }}
        />
      )}

      {/* Table card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                {canMutate && (
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartialSelected;
                      }}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left font-bold">Request</th>
                <th className="px-4 py-3 text-left font-bold">Contact</th>
                <th className="px-4 py-3 text-left font-bold">Preview</th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-left font-bold">Assigned</th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td
                      colSpan={canMutate ? 7 : 6}
                      className="px-4 py-4"
                    >
                      <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={canMutate ? 7 : 6} className="px-6 py-14">
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <FiInbox />
                      </div>
                      <div className="text-slate-600 font-semibold">No requests found</div>
                      <div className="text-slate-500 text-sm">
                        Try changing filters or search.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => {
                  const id = resolveId(row);
                  const focused = focusedId === id;

                  const st = getStatus(row);
                  const src = getSource(row);
                  const created = row?.createdAt || row?.created || null;
                  const ass = getAssignedTo(row);

                  const preview = getPreview(row);
                  const previewLine = preview ? preview.split('\n')[0] : '';

                  return (
                    <tr
                      key={id}
                      className={`group transition ${focused ? 'bg-blue-50/40' : 'hover:bg-blue-50/40'}`}
                      onMouseEnter={() => setFocusedId(id)}
                    >
                      {canMutate && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.includes(id)}
                            onChange={() => toggleRow(id)}
                          />
                        </td>
                      )}

                      <td
                        className="px-4 py-3 whitespace-nowrap cursor-pointer"
                        onClick={() => navigate(`/requests/${id}`)}
                        title="View details"
                      >
                        <div className="font-semibold text-slate-900">
                          {getReqNumber(row)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {fmtDateTime(created)} • {src}
                        </div>
                      </td>

                      <td
                        className="px-4 py-3 min-w-[220px] cursor-pointer"
                        onClick={() => navigate(`/requests/${id}`)}
                      >
                        <div className="font-semibold text-slate-900 truncate">
                          {getContactName(row)}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {getEmail(row) || getPhone(row) || '—'}
                        </div>
                      </td>

                      <td
                        className="px-4 py-3 min-w-[320px] cursor-pointer"
                        onClick={() => navigate(`/requests/${id}`)}
                      >
                        <div className="text-slate-700 truncate">
                          {previewLine || <span className="text-slate-300">—</span>}
                        </div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {canMutate ? (
                          <select
                            className="text-xs font-semibold px-2 py-1 rounded-lg border border-slate-200 bg-white"
                            value={st}
                            onChange={(e) => patchRequest(id, { status: e.target.value })}
                            title="Change status"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold ${statusChipClass(st)}`}>
                            {statusLabel(st)}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-semibold text-slate-900">
                          {assignedName(ass)}
                        </div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        {canMutate ? (
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        ) : (
                          <button
                            className="px-3 py-1.5 rounded-xl border text-sm"
                            onClick={() => navigate(`/requests/${id}`)}
                          >
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : pageRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No requests found.</div>
          ) : (
            pageRows.map((row) => {
              const id = resolveId(row);
              const st = getStatus(row);
              const created = row?.createdAt || row?.created || null;
              const src = getSource(row);
              const ass = getAssignedTo(row);
              const preview = getPreview(row);
              const previewLine = preview ? preview.split('\n')[0] : '';

              return (
                <div
                  key={id}
                  className="p-4 hover:bg-blue-50/60 transition"
                  onClick={() => navigate(`/requests/${id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {getContactName(row)}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {getReqNumber(row)} • {fmtDateTime(created)} • {src}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${statusChipClass(st)}`}>
                      {statusLabel(st)}
                    </span>
                  </div>

                  <div className="mt-2 text-sm text-slate-700 truncate">
                    {previewLine || <span className="text-slate-300">—</span>}
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-xs text-slate-600">
                      Assigned: <span className="font-semibold">{assignedName(ass)}</span>
                    </div>

                    {canMutate ? (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuRow(row);
                          setMenuAnchor(e.currentTarget);
                        }}
                      >
                        <FiMoreHorizontal className="inline -mt-0.5 mr-1" /> Actions
                      </button>
                    ) : (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/requests/${id}`);
                        }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold">{pageRows.length}</span> of{' '}
            <span className="font-semibold">{sorted.length}</span> filtered requests
            {filtered.length !== requests.length && <> (from {requests.length} total)</>}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{currentPage}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          canMutate={canMutate}
          canDelete={canDelete}
          users={users}
          meId={meId}
          onView={(id) => navigate(`/requests/${id}`)}
          onSetStatus={(id, status) => patchRequest(id, { status })}
          onAssign={(id, userIdOrNull) => patchRequest(id, { assignedTo: userIdOrNull })}
          onDelete={deleteRequest}
        />
      )}
    </div>
  );
}
